import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Radio, Result, Row, Select, Typography } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import Calendar from "containers/Company/EventBooking/Booking/Calendar";
import { bookingErrorMessage } from "containers/Company/EventBooking/bookingRequest";
import { BookingSeason, PriceTier } from "containers/Company/EventBooking/interface";
import PriceTable from "containers/Company/EventBooking/PriceTable/PriceTable";
import { formatWeeks, getSemesterWeeks } from "containers/Company/EventBooking/pricing";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

dayjs.extend(customParseFormat);

const { Title } = Typography;

const CreateOrEditBookingSeason: React.FC = () => {
  const [form] = Form.useForm();
  const [is_fall, setIsFall] = useState<boolean>();
  const [preview, setPreview] = useState<BookingSeason>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [season, setSeason] = useState<BookingSeason>();
  const { id } = useParams<{ id: string }>();

  const watchedSemesterStart: Dayjs | undefined = Form.useWatch("semester_start", form);
  const watchedSemesterEnd: Dayjs | undefined = Form.useWatch("semester_end", form);
  const priceTiers: Partial<PriceTier>[] | undefined = Form.useWatch("price_tiers", form);
  const semesterWeeks =
    watchedSemesterStart && watchedSemesterEnd
      ? getSemesterWeeks(watchedSemesterStart.toDate(), watchedSemesterEnd.toDate())
      : [];

  const history = useHistory();

  useEffect(() => {
    if (!id) return;
    setFetching(true);
    axios
      .get<BookingSeason>(`/booking-seasons/${id}/`)
      .then(response => {
        setSeason(response.data);
      })
      .finally(() => setFetching(false));
  }, [id]);

  const update_season = (payload: Record<string, unknown>) => {
    axios
      .put(`/booking-seasons/${season?.id}/`, payload)
      .then(() => {
        message("success", "Bookingsesongen ble oppdatert");
        history.push(`/admin/tools/booking-seasons/`);
      })
      .catch(e => message("error", bookingErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  const create_season = (payload: Record<string, unknown>) => {
    axios
      .post("/booking-seasons/create_season/", payload)
      .then(() => {
        message("success", "Bookingsesongen ble opprettet");
        history.push(`/admin/tools/booking-seasons/`);
      })
      .catch(e => message("error", bookingErrorMessage(e)))
      .finally(() => setLoading(false));
  };

  const onFinish = () => {
    if (!preview) return;
    setLoading(true);

    const now = new Date().toISOString();
    const payload = { ...preview, updated_at: now, ...(season ? {} : { created_at: now }) };

    if (season) {
      update_season(payload);
    } else {
      create_season(payload);
    }
  };

  useEffect(() => {
    if (!season) return;
    setIsFall(season.is_fall);
    form.setFieldsValue({
      is_fall: season.is_fall,
      year: dayjs(String(season.year), "YYYY"),
      booking_open: dayjs(season.booking_open),
      booking_end: dayjs(season.booking_end),
      semester_start: dayjs(season.semester_start),
      semester_end: dayjs(season.semester_end),
      unavailable_dates_collaboration_partner:
        season.unavailable_dates_collaboration_partner && season.unavailable_dates_collaboration_partner?.length > 0
          ? season.unavailable_dates_collaboration_partner.map(date => dayjs(date))
          : null,
      unavailable_dates_other:
        season.unavailable_dates_other && season.unavailable_dates_other?.length > 0
          ? season.unavailable_dates_other.map(date => dayjs(date))
          : null,
      unavailable_date_ot_start: season.unavailable_date_ot_start ? dayjs(season.unavailable_date_ot_start) : null,
      unavailable_date_ot_end: season.unavailable_date_ot_end ? dayjs(season.unavailable_date_ot_end) : null,
      unavailable_date_kd_start: season.unavailable_date_kd_start ? dayjs(season.unavailable_date_kd_start) : null,
      unavailable_date_kd_end: season.unavailable_date_kd_end ? dayjs(season.unavailable_date_kd_end) : null,
      price_tiers: season.price_tiers,
    });
  }, [season, form]);

  const handlePreview = () => {
    if (preview) return setPreview(undefined);

    form
      .validateFields()
      .then(() => {
        const formData = form.getFieldsValue();
        const unavailableDatesCollaborationPartner =
          formData.unavailable_dates_collaboration_partner &&
          formData.unavailable_dates_collaboration_partner.length > 0
            ? formData.unavailable_dates_collaboration_partner.map((date: Dayjs) => date.format("YYYY-MM-DD"))
            : [];
        const unavailableDatesOther =
          formData.unavailable_dates_other && formData.unavailable_dates_other.length > 0
            ? formData.unavailable_dates_other.map((date: Dayjs) => date.format("YYYY-MM-DD"))
            : [];

        const previewData = {
          ...formData,
          is_fall,
          year: formData.year.format("YYYY"),
          booking_open: formData.booking_open.format("YYYY-MM-DD HH:mm"),
          booking_end: formData.booking_end.format("YYYY-MM-DD HH:mm"),
          semester_start: formData.semester_start.format("YYYY-MM-DD"),
          semester_end: formData.semester_end.format("YYYY-MM-DD"),
          unavailable_dates_collaboration_partner: unavailableDatesCollaborationPartner,
          unavailable_dates_other: unavailableDatesOther,
          unavailable_date_ot_start: formData.unavailable_date_ot_start?.format("YYYY-MM-DD") || null,
          unavailable_date_ot_end: formData.unavailable_date_ot_end?.format("YYYY-MM-DD") || null,
          unavailable_date_kd_start: formData.unavailable_date_kd_start?.format("YYYY-MM-DD") || null,
          unavailable_date_kd_end: formData.unavailable_date_kd_end?.format("YYYY-MM-DD") || null,
          booked_days: season?.booked_days || [],
          price_tiers: (formData.price_tiers as PriceTier[]).map(tier => ({
            ...tier,
            name: tier.name.trim(),
            weeks: [...tier.weeks].sort((a, b) => a - b),
          })),
        };
        setPreview(previewData);
      })
      .catch((e: { errorFields?: { name: (string | number)[]; errors: string[] }[] }) => {
        const errorFields = e.errorFields ?? [];
        const errors = Array.from(new Set(errorFields.flatMap(field => field.errors)));
        message(
          "error",
          errors.length > 0
            ? `Skjemaet inneholder feil: ${errors.join(". ")}`
            : "Skjemaet inneholder feil. Se over feltene og prøv igjen."
        );
        if (errorFields.length > 0) {
          form.scrollToField(errorFields[0].name, { behavior: "smooth", block: "center" });
        }
      });
  };

  if (id && !season && !fetching)
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Result title="Fant ikke bookingsesong" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />
      </div>
    );

  return (
    <Card style={{ width: "100%" }}>
      <Title level={2}>{id ? "Rediger bookingsesong" : "Opprett bookingsesong"}</Title>
      <Form
        form={form}
        disabled={!!preview || fetching}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ price_tiers: [{ name: "Trinn 1", weeks: [], thursday_surcharge: 0 }] }}
      >
        <Row>
          <Col span={24}>
            <Title level={5}>Kalenderår og semester for bookingsesong</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="År"
              name="year"
              tooltip="Angi hvilket kalenderår bedriftspresentasjonene skal arrangeres."
              rules={[{ required: true, message: "Vennligst velg år" }]}
            >
              <DatePicker picker="year" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Semester"
              name="is_fall"
              tooltip="Angi hvilket semester bedriftspresentasjonene skal arrangeres."
              rules={[{ required: true, message: "Vennligst velg semester" }]}
            >
              <Radio.Group onChange={e => setIsFall(e.target.value)}>
                <Radio value>Høst</Radio>
                <Radio value={false}>Vår</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Bookingperiode</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Booking åpner"
              name="booking_open"
              tooltip="Angi dato og klokkeslett for når bookingen åpner for bedrifter. Dette er tidspunktet når bedriften får mulighet til å booke en dag, ikke dagen de skal ha presentasjonen."
              rules={[
                { required: true, message: "Vennligst velg dato og klokkeslett for når bookingen åpner" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const semesterStart = getFieldValue("semester_start");
                    if (semesterStart && value && value.isAfter(semesterStart)) {
                      return Promise.reject(new Error("Datoen må være før sesongstart"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker
                showTime={{ defaultValue: dayjs("12:00:00", "HH:mm:ss"), format: "HH:mm" }}
                showSecond={false}
                showNow={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Booking stenger"
              name="booking_end"
              tooltip="Angi dato og klokkeslett for når bookingen stenger for bedrifter. Dette er tidspunktet når bedriften ikke lenger kan booke en dag."
              rules={[
                { required: true, message: "Vennligst velg dato for når bookingen stenger" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("booking_open");
                    if (startDate && value && value.isBefore(startDate)) {
                      return Promise.reject(new Error("Dato for stenging må være senere enn åpningsdatoen."));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker
                showTime={{ defaultValue: dayjs("12:00:00", "HH:mm:ss"), format: "HH:mm" }}
                showSecond={false}
                showNow={false}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Sesongens varighet</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Sesongstart"
              name="semester_start"
              tooltip="Angi første dato som er mulig å booke."
              rules={[
                { required: true, message: "Vennligst velg første dato som er mulig å booke" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const year = getFieldValue("year");
                    const fall = getFieldValue("is_fall");
                    if (year && value && value.year() !== year.year()) {
                      return Promise.reject(new Error("Datoen må være i året du har valgt"));
                    }
                    if (fall !== undefined && fall !== null && fall === true && value && value.month() < 7) {
                      return Promise.reject(new Error("Datoen må være i høstsemesteret"));
                    }
                    if (fall !== undefined && fall !== null && fall === false && value && value.month() > 6) {
                      return Promise.reject(new Error("Datoen må være i vårsemesteret"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label="Sesongslutt"
              name="semester_end"
              tooltip="Angi siste dato som er mulig å booke."
              rules={[
                { required: true, message: "Vennligst velg siste dato som er mulig å booke" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("semester_start");
                    if (startDate && value && value.isBefore(startDate)) {
                      return Promise.reject(new Error("Sluttdato må være senere enn startdato"));
                    }
                    const year = getFieldValue("year");
                    const fall = getFieldValue("is_fall");
                    if (year && value && value.year() !== year.year()) {
                      return Promise.reject(new Error("Datoen må være i året du har valgt"));
                    }
                    if (fall !== undefined && fall !== null && fall === true && value && value.month() < 7) {
                      return Promise.reject(new Error("Datoen må være i høstsemesteret"));
                    }
                    if (fall !== undefined && fall !== null && fall === false && value && value.month() > 6) {
                      return Promise.reject(new Error("Datoen må være i vårsemesteret"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Reserverte og utilgjengelige datoer</Title>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.List name="unavailable_dates_collaboration_partner">
              {(fields, { add, remove }) => (
                // TODO We can consider upgrading antd to 5.14.0 to use <DatePicker multiple />
                <>
                  <Form.Item
                    label="Datoer reservert av SP"
                    required={false}
                    tooltip="Samarbeidspartnere kan reservere dag for bedriftspresentasjon før det offisielle innsalget. Angi disse datoene."
                    style={{ marginBottom: "0px" }}
                  >
                    {fields.map(({ key, name }) => (
                      <div key={key} style={{ width: "100%", marginBottom: "5px" }}>
                        <Form.Item
                          name={name}
                          rules={[
                            { required: true, message: "Angi en dato eller slett dette feltet." },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const semesterStart = getFieldValue("semester_start");
                                const semesterEnd = getFieldValue("semester_end");
                                if (semesterStart && value && value.isBefore(semesterStart)) {
                                  return Promise.reject(new Error("Datoen må være etter sesongstart"));
                                }
                                if (semesterEnd && value && value.isAfter(semesterEnd)) {
                                  return Promise.reject(new Error("Datoen må være før sesongslutt"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          noStyle
                        >
                          <DatePicker />
                        </Form.Item>
                        <Form.Item noStyle>
                          <Button
                            type="text"
                            size="small"
                            title="Fjern dato"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Form.Item>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add(null)}
                      icon={<PlusOutlined />}
                      style={{ marginBottom: "10px" }}
                    >
                      Legg til ny dato
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.List name="unavailable_dates_other">
              {(fields, { add, remove }) => (
                // TODO We can consider upgrading antd to 5.14.0 to use <DatePicker multiple />
                <>
                  <Form.Item
                    label="Andre utilgjengelige datoer"
                    required={false}
                    tooltip="Angi datoer som av andre grunner ikke kan bookes. Dette kan være pga. UKA, arrangementer av ESTIEM, heligdager, eller lignende."
                    style={{ marginBottom: "0px" }}
                  >
                    {fields.map(({ key, name }) => (
                      <div key={key} style={{ width: "100%", marginBottom: "5px" }}>
                        <Form.Item
                          name={name}
                          rules={[
                            { required: true, message: "Angi en dato eller slett dette feltet." },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const semesterStart = getFieldValue("semester_start");
                                const semesterEnd = getFieldValue("semester_end");
                                if (semesterStart && value && value.isBefore(semesterStart)) {
                                  return Promise.reject(new Error("Datoen må være etter sesongstart"));
                                }
                                if (semesterEnd && value && value.isAfter(semesterEnd)) {
                                  return Promise.reject(new Error("Datoen må være før sesongslutt"));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          noStyle
                        >
                          <DatePicker />
                        </Form.Item>
                        <Form.Item noStyle>
                          <Button
                            type="text"
                            size="small"
                            title="Fjern dato"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Form.Item>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add(null)}
                      icon={<PlusOutlined />}
                      style={{ marginBottom: "10px" }}
                    >
                      Legg til ny dato
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        {is_fall ? (
          <Row>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="KD startdato"
                name="unavailable_date_kd_start"
                tooltip="Angi første dag som er utilgjengelig som følge av KarriereDagene. Merk at hele uken normalt sett holdes av."
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const semesterStart = getFieldValue("semester_start");
                      const semesterEnd = getFieldValue("semester_end");
                      if (semesterStart && value && value.isBefore(semesterStart)) {
                        return Promise.reject(new Error("Datoen må være etter sesongstart"));
                      }
                      if (semesterEnd && value && value.isAfter(semesterEnd)) {
                        return Promise.reject(new Error("Datoen må være før sesongslutt"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="KD sluttdato"
                name="unavailable_date_kd_end"
                tooltip="Angi siste dag som er utilgjengelig som følge av KarriereDagene. Merk at hele uken normalt sett holdes av."
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const startDate = getFieldValue("unavailable_date_kd_start");
                      if (startDate && !value) {
                        return Promise.reject(new Error("Vennligst velg sluttdato for KarriereDagene"));
                      }
                      if (startDate && value && value.isBefore(startDate)) {
                        return Promise.reject(new Error("Sluttdato må være senere enn startdato"));
                      }
                      const semesterStart = getFieldValue("semester_start");
                      const semesterEnd = getFieldValue("semester_end");
                      if (semesterStart && value && value.isBefore(semesterStart)) {
                        return Promise.reject(new Error("Datoen må være etter sesongstart"));
                      }
                      if (semesterEnd && value && value.isAfter(semesterEnd)) {
                        return Promise.reject(new Error("Datoen må være før sesongslutt"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          is_fall === false && (
            <Row>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  label="OT startdato"
                  name="unavailable_date_ot_start"
                  tooltip="Angi første dag som er utilgjengelig som følge av OsloTuren."
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const semesterStart = getFieldValue("semester_start");
                        const semesterEnd = getFieldValue("semester_end");
                        if (semesterStart && value && value.isBefore(semesterStart)) {
                          return Promise.reject(new Error("Datoen må være etter sesongstart"));
                        }
                        if (semesterEnd && value && value.isAfter(semesterEnd)) {
                          return Promise.reject(new Error("Datoen må være før sesongslutt"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  label="OT sluttdato"
                  name="unavailable_date_ot_end"
                  tooltip="Angi siste dag som er utilgjengelig som følge av OsloTuren."
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const startDate = getFieldValue("unavailable_date_ot_start");
                        if (startDate && !value) {
                          return Promise.reject(new Error("Vennligst velg sluttdato for OsloTuren"));
                        }
                        if (startDate && value && value.isBefore(startDate)) {
                          return Promise.reject(new Error("Sluttdato må være senere enn startdato"));
                        }
                        const semesterStart = getFieldValue("semester_start");
                        const semesterEnd = getFieldValue("semester_end");
                        if (semesterStart && value && value.isBefore(semesterStart)) {
                          return Promise.reject(new Error("Datoen må være etter sesongstart"));
                        }
                        if (semesterEnd && value && value.isAfter(semesterEnd)) {
                          return Promise.reject(new Error("Datoen må være før sesongslutt"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker />
                </Form.Item>
              </Col>
            </Row>
          )
        )}
        <Row>
          <Col span={24}>
            <Title level={5}>Pristrinn</Title>
            <Typography.Paragraph type="secondary">
              Del sesongens uker inn i pristrinn. Hver uke må være med i nøyaktig ett trinn. Torsdagstillegget legges på
              prisen for torsdager i trinnet. Prisene er i hele kroner ekskl. mva, og vises som en pristabell for
              bedriftene.
            </Typography.Paragraph>
          </Col>
          <Col span={24}>
            <Form.List
              name="price_tiers"
              rules={[
                {
                  validator(_, tiers: Partial<PriceTier>[] | undefined) {
                    if (!tiers || tiers.length === 0) {
                      return Promise.reject(new Error("Legg til minst ett pristrinn"));
                    }
                    const covered = new Set(tiers.flatMap(tier => tier?.weeks ?? []));
                    // Weeks picked before the semester dates were changed can end up outside the season
                    const outside = Array.from(covered)
                      .filter(week => !semesterWeeks.includes(week))
                      .sort((a, b) => a - b);
                    if (outside.length > 0) {
                      return Promise.reject(
                        new Error(`Uke ${outside.join(", ")} er utenfor sesongen. Fjern dem fra pristrinnene.`)
                      );
                    }
                    const missing = semesterWeeks.filter(week => !covered.has(week));
                    if (missing.length > 0) {
                      return Promise.reject(new Error(`Uke ${missing.join(", ")} mangler pristrinn`));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(({ key, name }) => {
                    // Weeks already used by other tiers can't be picked again
                    const takenWeeks = new Set(
                      (priceTiers ?? []).flatMap((tier, i) => (i === name ? [] : tier?.weeks ?? []))
                    );
                    return (
                      <Row key={key} gutter={12} align="top">
                        <Col xs={24} md={5}>
                          <Form.Item
                            label="Navn"
                            name={[name, "name"]}
                            rules={[{ required: true, whitespace: true, message: "Angi navn" }]}
                          >
                            <Input placeholder="F.eks. Trinn 1" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={9}>
                          <Form.Item
                            label="Uker"
                            name={[name, "weeks"]}
                            rules={[{ required: true, message: "Velg minst én uke" }]}
                          >
                            <Select
                              mode="multiple"
                              placeholder={semesterWeeks.length ? "Velg uker" : "Velg sesongstart og -slutt først"}
                              options={semesterWeeks.map(week => ({
                                value: week,
                                label: `Uke ${week}`,
                                disabled: takenWeeks.has(week),
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={11} md={4}>
                          <Form.Item
                            label="Pris (kr)"
                            name={[name, "price"]}
                            rules={[{ required: true, message: "Angi pris" }]}
                          >
                            <InputNumber<number>
                              min={0}
                              step={100}
                              precision={0}
                              formatter={value => (value ? Number(value).toLocaleString("no") : "")}
                              parser={value => Number((value ?? "").replace(/\D/g, ""))}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={11} md={5}>
                          <Form.Item
                            label="Torsdagstillegg (kr)"
                            name={[name, "thursday_surcharge"]}
                            rules={[{ required: true, message: "Angi tillegg (0 for ingen)" }]}
                          >
                            <InputNumber<number>
                              min={0}
                              step={100}
                              precision={0}
                              formatter={value =>
                                value !== undefined && value !== null ? Number(value).toLocaleString("no") : ""
                              }
                              parser={value => Number((value ?? "").replace(/\D/g, ""))}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={2} md={1} style={{ paddingTop: "30px" }}>
                          <Button
                            type="text"
                            size="small"
                            title="Fjern pristrinn"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Col>
                      </Row>
                    );
                  })}
                  <Button
                    type="dashed"
                    onClick={() => add({ name: `Trinn ${fields.length + 1}`, weeks: [], thursday_surcharge: 0 })}
                    icon={<PlusOutlined />}
                    style={{ marginBottom: "10px" }}
                  >
                    Legg til pristrinn
                  </Button>
                  <Form.ErrorList errors={errors} />
                  {semesterWeeks.length > 0 && (
                    <Typography.Paragraph type="secondary">
                      Sesongen går over {formatWeeks(semesterWeeks)}.
                    </Typography.Paragraph>
                  )}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Title level={5}>Forhåndsvisning og opprettelse</Title>
          </Col>
          <Col span={24}>
            <Button type="dashed" onClick={handlePreview} disabled={fetching} style={{ width: "100%" }}>
              {preview ? "Skjul forhåndsvisning og fortsett å redigere" : "Forhåndsvis bookingkalender"}
            </Button>
          </Col>
          <Col span={24}>
            {preview && (
              <Row justify="center">
                <Col span={24}>
                  <PriceTable tiers={preview.price_tiers} />
                </Col>
                <Calendar
                  bookingSeason={preview}
                  setSelectedDay={day => {
                    return day;
                  }}
                  loading={false}
                />
              </Row>
            )}
          </Col>
          {preview && (
            <Col span={24}>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!preview}
                  loading={loading}
                  style={{ width: "100%" }}
                >
                  {id ? "Lagre endringer" : "Opprett bookingsesongen"}
                </Button>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Card>
  );
};

export default CreateOrEditBookingSeason;
